import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NFT_IMAGES, TierKey } from '../../constants';
import { tierIdToTierKey } from '../../utils/utils';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { MovesEnum } from 'shared/constants/shared-enums';
import { useQuestsStore } from 'shared/store/questsStore';

import nftTitle from 'assets/png/nft-title.png?format=webp;avif&as=picture';

type Props = {
  topPct?: number;
  rightPct?: number;
  blockWidthPct?: number;
  gapPct?: number;
};

const DEFAULT_TIER: TierKey = 'bronze';

export const TopNFTSPreview: React.FC<Props> = ({
  topPct = 16,
  rightPct = 3,
  blockWidthPct = 60,
  gapPct = 4,
}) => {
  const { t } = useTranslation();

  const bestTierPerMove = useQuestsStore((s) => s.bestTierPerMove);

  const { rockKey, paperKey, scissorsKey } = useMemo(() => {
    const rockId = bestTierPerMove?.[MovesEnum.Rock] ?? null;
    const paperId = bestTierPerMove?.[MovesEnum.Paper] ?? null;
    const scisId = bestTierPerMove?.[MovesEnum.Scissors] ?? null;

    const r = tierIdToTierKey(rockId) || DEFAULT_TIER;
    const p = tierIdToTierKey(paperId) || DEFAULT_TIER;
    const s = tierIdToTierKey(scisId) || DEFAULT_TIER;

    return { rockKey: r, paperKey: p, scissorsKey: s };
  }, [bestTierPerMove]);

  const items = useMemo(
    () => [
      { label: t('nft.rock', 'Rock'), key: rockKey as TierKey, field: MovesEnum.Rock },
      { label: t('nft.paper', 'Paper'), key: paperKey as TierKey, field: MovesEnum.Paper },
      {
        label: t('nft.scissors', 'Scissors'),
        key: scissorsKey as TierKey,
        field: MovesEnum.Scissors,
      },
    ],
    [t, rockKey, paperKey, scissorsKey],
  );

  return (
    <>
      <h2 className="bg-gradient-to-b from-white to-[#B5E0FF] bg-clip-text text-center text-xl font-extrabold text-transparent md:text-2xl">
        {t('nft.progression')}
      </h2>

      <div className="relative w-full">
        <OptimizedImage imageData={nftTitle} alt="" className="block h-auto w-full" />

        <div className="absolute inset-0">
          <div
            className="absolute flex flex-col items-center"
            style={{
              top: `${topPct}%`,
              right: `${rightPct}%`,
              width: `${blockWidthPct}%`,
              gap: `${gapPct}%`,
            }}
          >
            <p className="bg-[linear-gradient(179deg,_#FFF_-12.42%,_#FFDE7A_116.87%)] bg-clip-text text-xl font-bold leading-none text-transparent drop-shadow-[0_2px_0_#6F4100] [-webkit-text-fill-color:transparent] [-webkit-text-stroke-color:#542A00] [-webkit-text-stroke-width:2px] sm:text-4xl sm:drop-shadow-[0_4px_0_#6F4100]">
              {t('nft.topNfts')}
            </p>
            <div className="mt-[3%] flex w-full justify-center" style={{ gap: `${gapPct}%` }}>
              {items.map(({ label, key, field }) => {
                const img = NFT_IMAGES[key]?.[field];
                if (!img) return null;
                return (
                  <OptimizedImage
                    key={`${field}-${key}`}
                    imageData={img}
                    alt={label}
                    className="h-auto w-[30%]"
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
